// Client-side resolution of a coded API error into display copy (COI-779,
// ADR-0032).
//
// The server answers every migrated route with
//   {"error": {"code", "message", "details", "request_id"}}
// and one canonical English message — i18n is client-side only. Each app
// declares which codes it renders itself, as a code -> translation key map,
// and the page carries a dictionary of translation key -> localised message
// for the request's language (rendered by pkg/errors/errori18n into a
// <script type="application/json" id="error-translations"> element).
//
// The fallback chain is deliberate and shallow:
//   1. the app maps the code AND the page localises that key -> localised copy
//   2. otherwise the server's message -> always correct, just not localised
//   3. otherwise the caller's fallback
//
// Step 2 is what makes a newly added server code safe: it renders the server's
// wording until the frontend is taught about it, instead of rendering nothing.

export const TRANSLATIONS_ELEMENT_ID = "error-translations";
export const KEY_PREFIX = "errors.";

// translationKeyFor is the convention every locale file follows:
// PAYMENT_FAILED lives at errors.payment_failed.
export function translationKeyFor(code) {
  if (!code || typeof code !== "string") return "";
  return KEY_PREFIX + code.toLowerCase();
}

let cachedTranslations = null;

// readTranslations parses the dictionary the page carries. A page without one
// (a stream fragment rendered outside a full document, a test) resolves to an
// empty dictionary, which falls the whole chain through to the server message.
export function readTranslations(doc) {
  const target = doc || (typeof document === "undefined" ? null : document);
  if (!target) return {};
  if (!doc && cachedTranslations) return cachedTranslations;

  let parsed = {};
  const element = target.getElementById(TRANSLATIONS_ELEMENT_ID);
  if (element && element.textContent) {
    try {
      const candidate = JSON.parse(element.textContent);
      if (candidate && typeof candidate === "object") parsed = candidate;
    } catch {
      // A malformed dictionary must not break error rendering — that would
      // swallow the error the customer is being told about.
      parsed = {};
    }
  }

  if (!doc) cachedTranslations = parsed;
  return parsed;
}

// resetTranslationsCache drops the memoised dictionary. Tests use it; so would
// a language switch that swaps the element without a full page load.
export function resetTranslationsCache() {
  cachedTranslations = null;
}

// errorEnvelope normalises the shapes an error reaches the frontend in — a
// parsed response body, an Error thrown by HttpService (which copies the
// envelope onto the error), or nothing — into one object.
export function errorEnvelope(source) {
  if (!source || typeof source !== "object") return null;

  const body = source.error;
  if (body && typeof body === "object") {
    return {
      code: body.code || "",
      message: body.message || "",
      details: body.details || null,
      requestId: body.request_id || "",
    };
  }

  // An Error carrying the envelope fields, or a legacy body: {"message": …} /
  // {"error": "…"}.
  return {
    code: source.code || "",
    message: source.message || (typeof body === "string" ? body : ""),
    details: source.details || null,
    requestId: source.requestId || source.request_id || "",
  };
}

// resolveErrorMessage returns the copy to show for source.
//
//   keys         code -> translation key map for this app (required to localise)
//   fallback     shown when neither a translation nor a server message exists
//   translations dictionary override, for tests and for stream fragments
export function resolveErrorMessage(source, { keys = {}, fallback = "", translations } = {}) {
  const envelope = errorEnvelope(source);
  if (!envelope) return fallback;

  const key = keys[envelope.code];
  if (key) {
    const dictionary = translations || readTranslations();
    const localised = dictionary[key];
    if (localised) return localised;
  }

  return envelope.message || fallback;
}

// createErrorResolver binds an app's map once, so controllers call
// translateError(error, fallback) without repeating it.
export function createErrorResolver(keys) {
  return function translateError(source, fallback = "") {
    return resolveErrorMessage(source, { keys, fallback });
  };
}
